import React, {
    useEffect,
    useState,
    useCallback,
    useMemo,
    useRef,
} from 'react';
import axios from 'axios';
import { navigate } from 'gatsby';
import yaml from 'js-yaml';
import ReCAPTCHA from 'react-google-recaptcha';
import {
    getLocalStorage,
    updateLocalStorage,
    queryBuilder,
} from '../../shared/Utils';
import { useLayoutContext } from '../../providers/LayoutProvider';
import { convertSelectOptions } from '../../shared/formUtils';
import { FormHeading } from '../../shared/headingBuilder';
import { ReachButton } from '../ReachButton/ReachButton';
import { AccentedText } from '../AccentedText/AccentedText';
import { DyamicFormElements } from './DynamicFormElements';
import {
    FORM_KITE_ELEMENTS_ENUM,
    IFormItems,
    IDynamicForm,
} from './IDynamicFormTypes';
import './DynamicForm.scss';

const DEFAULT_SUCCESS_PAGE_URL = `/contact-thank-you`;

export const DynamicForm = (props: IDynamicForm) => {
    if (!props.node) {
        return <></>;
    }
    const formRef = useRef(null);
    const { inputProspect, campaignTermsFilter } = useLayoutContext();
    const formItems: IFormItems[] = [];
    const {
        node,
        submissionUrl,
        dynamicFormID,
        disclaimer,
        formHeadingData,
        successPageUrl,
    } = props;
    const [dynamicFormStage, setDynamicFormStage] = useState(1);
    const [disabledSubmit, setDisabledSubmit] = useState(true);
    const [errorFlag, setErrorFlag] = useState(true);
    const [captchaReady, setCaptchaReady] = useState(false);
    const [success, setSuccess] = useState(false);
    const [fail, setFail] = useState(false);
    const [recaptchaLoaded, setRecaptchaLoaded] = useState(false);
    const [formError, setFormError] = useState(``);
    const [stageCounter, setStageCounter] = useState(1);
    const formLayout = yaml.load(node);
    const queryParametersToString = queryBuilder(campaignTermsFilter, true);
    let submitText = `Submit`;

    const handleRecaptchaLoad = useCallback(() => {
        setRecaptchaLoaded(true);
    }, []);

    const handleRecaptchaChange = (resp) => {
        resp ? setCaptchaReady(true) : setCaptchaReady(false);
    };

    useEffect(() => {
        !errorFlag && captchaReady
            ? setDisabledSubmit(false)
            : setDisabledSubmit(true);
    }, [errorFlag, captchaReady]);

    for (const key in formLayout) {
        const item = formLayout[key];
        const itemLabel = key.split(`_`).join(` `).toString();

        if (item[`#type`] === `webform_actions`) {
            submitText = item[`#submit__label`] ?? `Connect With An Expert`;
        }

        useMemo(() => {
            formItems.push({
                name: key,
                label: itemLabel,
                id: `${item[`#type`]}-${key}`,
                formItemId: ``,
                type: item[`#type`],
                kiteComponent: FORM_KITE_ELEMENTS_ENUM[item[`#type`]],
                required: item[`#required`] ?? false,
                formItemName: item[`#title`],
                multiple: item[`#multiple`] || false,
                multiline: item[`#type`] === `textarea`,
                inputStage: formHeadingData.static
                    ? 1
                    : item[`#dataStage`] ?? null,
                inputOptions: convertSelectOptions(item[`#options`], true),
                optionsFilter: convertSelectOptions(item[`#options`], false),
                emptyValue: item[`#empty_value`] || ``,
                checked: false,
                defaultValue: item[`#default_value`] || ``,
            });
        }, [formLayout]);
    }

    useEffect(() => {
        if (typeof window !== `undefined`) {
            const returnDynamicFormStageStorage =
                getLocalStorage(`dynamicFormStages`);
            if (
                returnDynamicFormStageStorage &&
                returnDynamicFormStageStorage[dynamicFormID]
            ) {
                setDynamicFormStage(
                    Number(returnDynamicFormStageStorage[dynamicFormID])
                );
            } else {
                setDynamicFormStage(1);
                updateLocalStorage(`dynamicFormStages`, dynamicFormID, 1);
            }
        }
    }, []);

    const getDefaultValuesFromHiddenFields = (formItems: IFormItems[]) => {
        const defaultValues = {};
        formItems.forEach((formItem: IFormItems) => {
            if (formItem.type === `hidden`)
                defaultValues[formItem.name] = formItem.defaultValue;
        });
        return defaultValues;
    };

    const handleFormSubmit = async (event) => {
        event.preventDefault();

        if (typeof window !== `undefined`) {
            const captchaResponse = window.grecaptcha?.getResponse();

            try {
                if (!captchaResponse) {
                    setFail(true);
                    setSuccess(false);
                } else {
                    await axios
                        .post(`/form-submissions`, {
                            formHandler: `${submissionUrl}`,
                            formData: {
                                ...inputProspect,
                                ...getDefaultValuesFromHiddenFields(formItems),
                            },
                            formQueryParams: queryParametersToString,
                        })
                        .then((res) => {
                            if (res.status === 200) {
                                setDisabledSubmit(true);
                                if (formHeadingData.static) {
                                    setDynamicFormStage(1);
                                } else {
                                    const stageCounter =
                                        dynamicFormStage >= 4
                                            ? 4
                                            : dynamicFormStage + 1;
                                    setDynamicFormStage(stageCounter);
                                    updateLocalStorage(
                                        `dynamicFormStages`,
                                        dynamicFormID,
                                        stageCounter
                                    );
                                }
                                setFail(false);
                                setSuccess(true);
                                setFormError(``);

                                navigate(
                                    successPageUrl ?? DEFAULT_SUCCESS_PAGE_URL
                                );
                            } else {
                                console.error(
                                    `Post not submitted - please try again later`
                                );
                                setFail(true);
                                setSuccess(false);
                                setFormError(
                                    `Form not submitted: ${res}. Please try again later.`
                                );
                            }
                        })
                        .catch((err) => {
                            setFormError(
                                `Error posting form: ${err.message}. Please try again later.`
                            );
                            console.error(`Axios post failed: `, err);
                        });
                }
            } catch (err) {
                setFail(true);
                setFormError(
                    `Error submitting form: ${err.message}. Please try again later.`
                );
                console.error(`Error submitting form: `, err);
            }
        }
    };

    return (
        <div className="dynamic-form">
            {formHeadingData?.formHeadingText && (
                <div
                    className={`dynamic-form-heading ${
                        formHeadingData?.align ? formHeadingData?.align : ``
                    }`}
                >
                    <FormHeading
                        formHeadingTag={formHeadingData?.formHeadingTag}
                    >
                        <AccentedText>
                            {formHeadingData?.formHeadingText}
                        </AccentedText>
                    </FormHeading>
                    {formHeadingData?.formHeadingSubtitle && (
                        <p className={`typestack-body-1 form-subtitle`}>
                            {formHeadingData?.formHeadingSubtitle}
                        </p>
                    )}
                </div>
            )}
            <div className={`form-wrapper`}>
                <form
                    ref={formRef}
                    className={`kite-theme--light`}
                    action="/form-submissions"
                    method="post"
                    autoComplete="on"
                >
                    <p className={`typestack-caption`}>All fields required.</p>
                    <fieldset>
                        <div className="visually-hidden hidden" hidden>
                            <label htmlFor={`pardot_extra_field`}>
                                Comments
                            </label>
                            <input
                                type="text"
                                id="pardot_extra_field"
                                name="pardot_extra_field"
                                value=""
                                autoComplete="off"
                                onChange={(e) => {
                                    if (e.target.value) {
                                        setDisabledSubmit(true);
                                    }
                                }}
                            />
                        </div>
                        {formItems &&
                            DyamicFormElements(
                                formItems,
                                dynamicFormStage,
                                errorFlag,
                                formHeadingData.static,
                                setErrorFlag
                            )}
                        <ReCAPTCHA
                            id="recaptchaEl"
                            sitekey="6LdXRYQqAAAAAOYnb2fIQZBeADNOlWY3sTrRIAkY"
                            onChange={handleRecaptchaChange}
                            onLoad={handleRecaptchaLoad}
                        />
                    </fieldset>
                    <div className="buttons">
                        <ReachButton
                            buttonStyle={`primary`}
                            disabled={disabledSubmit}
                            onClick={handleFormSubmit}
                            type={`button`}
                        >
                            {submitText}
                        </ReachButton>
                    </div>

                    {formError && (
                        <div
                            className={`kite-form-control kite-form-control--error`}
                        >
                            <p className={`kite-form-control__error`}>
                                {formError}
                            </p>
                        </div>
                    )}
                </form>
                <div className="disclaimer">
                    {disclaimer && (
                        <p className={`typestack-disclaimer`}>{disclaimer}</p>
                    )}
                    <a
                        href="https://www.spectrum.com/policies/privacy-policy"
                        target="_blank"
                    >
                        Privacy Policy
                    </a>
                </div>
            </div>
        </div>
    );
};
